/**
 * Application Entry Point
 *
 * Initializes Sentry, React, database, auth, and renders the app.
 */

import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ScrollToTop } from '@/components/ScrollToTop';
import { initDB, seedDefaultCategories } from '@services/db';
import { initTheme } from '@services/themes';
// i18n singleton: importing resolves the language (?lang= / localStorage /
// navigator) and sets document.documentElement.lang before first paint.
import '@/i18n';
import { AuthProvider } from '@services/auth';
import { PrivacyModeProvider } from '@hooks/usePrivacyMode';
import './styles/index.css';
import './styles/fonts.css';

// Initialize Sentry FIRST (before anything else)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
console.log('[SENTRY] DSN present:', !!sentryDsn, 'Prod mode:', import.meta.env.PROD);
console.log('[BUILD]', __BUILD_TS__);

Sentry.init({
  dsn: sentryDsn,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE || 'dev',
  enabled: import.meta.env.PROD, // Only in production
  sampleRate: 1.0,
  tracesSampleRate: 0.01, // 1% performance sampling
  sendDefaultPii: false, // CRITICAL: No PII for finance app
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    /^Non-Error promise rejection captured/,
    /^Extension context invalidated/,
    // Browser refused SW registration (old/bot Chrome, incognito, storage);
    // app still runs, only offline caching is lost for that visitor.
    /^Failed to register a ServiceWorker/,
    /^Rejected$/,
  ],
  // Disable console breadcrumbs - prevents console.log("amount:", 500) from leaking
  integrations: (integrations) =>
    integrations.filter((i) => i.name !== 'Breadcrumbs').concat(
      Sentry.breadcrumbsIntegration({ console: false })
    ),
  beforeSend(event) {
    // Scrub request data (may contain transaction info)
    if (event.request?.data) delete event.request.data;
    if (event.request?.cookies) delete event.request.cookies;
    console.log('[SENTRY] Sending event:', event.exception?.values?.[0]?.type || event.message || 'unknown');
    return event;
  },
});

console.log('[SENTRY] Initialized. Client:', Sentry.getClient() ? 'OK' : 'NULL');

// Debug mode detection
const DEBUG =
  import.meta.env.VITE_DEBUG === 'true' ||
  new URLSearchParams(window.location.search).has('debug');

if (DEBUG) {
  console.log('[APP] Debug mode enabled');
  console.log('[APP] Version:', __APP_VERSION__);
}

/**
 * Initialize the application.
 */
async function init(): Promise<void> {
  console.log('[APP] Initializing NinjaCash...');

  // Initialize theme from localStorage (before render to avoid flash)
  initTheme();

  // Initialize IndexedDB
  const dbReady = await initDB();
  if (!dbReady) {
    console.error('[APP] Failed to initialize database');
    // Show error UI instead of crashing
    renderError('Failed to initialize database. Please refresh the page.');
    return;
  }

  // Seed default categories if needed (mock data can be added via DevPage)
  await seedDefaultCategories();

  // Render the app
  renderApp();

  console.log('[APP] NinjaCash ready');
}

/**
 * Render the main application.
 */
function renderApp(): void {
  const root = document.getElementById('root');
  if (!root) {
    console.error('[APP] Root element not found');
    return;
  }

  // Remove the loading indicator class
  root.classList.remove('app-loading');

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <PrivacyModeProvider>
            <App />
          </PrivacyModeProvider>
        </AuthProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
}

/**
 * Render an error message if initialization fails.
 */
function renderError(message: string): void {
  const root = document.getElementById('root');
  if (!root) return;

  root.innerHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      background: #0d1117;
      color: #c9d1d9;
      font-family: system-ui, sans-serif;
      text-align: center;
      padding: 2rem;
    ">
      <h1 style="color: #f85149; margin-bottom: 1rem;">[ERROR]</h1>
      <p style="max-width: 400px;">${message}</p>
      <button
        onclick="window.location.reload()"
        style="
          margin-top: 2rem;
          padding: 0.75rem 1.5rem;
          background: #238636;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 1rem;
        "
      >
        Refresh Page
      </button>
    </div>
  `;
}

// Start the application
init().catch((error) => {
  console.error('[APP] Initialization error:', error);
  const msg = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : '';
  renderError(`Error: ${msg}<br><br><small style="color:#888;word-break:break-all;">${stack}</small>`);
});
